import { BrowserRouter, Navigate, Route, Routes } from 'react-router';

import { AuthProvider } from './auth/AuthProvider.tsx';
import { RequireAnonymous, RequireAuth } from './auth/guards.tsx';
import { ToastProvider } from './components/index.ts';
import { AppShell } from './layout/AppShell.tsx';
import { AdminMetricsRoute } from './routes/AdminMetricsRoute.tsx';
import { DashboardRoute } from './routes/DashboardRoute.tsx';
import { EntriesRoute } from './routes/EntriesRoute.tsx';
import { EditAnaestheticRoute } from './routes/EditAnaestheticRoute.tsx';
import { LogCaseRoute } from './routes/LogCaseRoute.tsx';
import { LogCommunicationRoute } from './routes/LogCommunicationRoute.tsx';
import { LogEdtRoute } from './routes/LogEdtRoute.tsx';
import { LogProcedureRoute } from './routes/LogProcedureRoute.tsx';
import { LogRoute } from './routes/LogRoute.tsx';
import { LoginRoute } from './routes/LoginRoute.tsx';
import { ForgotPasswordRoute } from './routes/ForgotPasswordRoute.tsx';
import { PrivacyRoute, TermsRoute } from './routes/PlaceholderRoutes.tsx';
import { RegisterRoute } from './routes/RegisterRoute.tsx';
import { ResetPasswordRoute } from './routes/ResetPasswordRoute.tsx';
import { ImportRoute } from './routes/ImportRoute.tsx';
import { ReportRoute } from './routes/ReportRoute.tsx';
import { SettingsRoute } from './routes/SettingsRoute.tsx';
import { StyleguideRoute } from './routes/StyleguideRoute.tsx';
import { UpdateToast } from './pwa/UpdateToast.tsx';
import { SyncProvider } from './sync/SyncProvider.tsx';

export function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SyncProvider>
          <ToastProvider>
            <UpdateToast />
            <Routes>
              {/* Readable without an account, and linked from registration. */}
              <Route path="/privacy" element={<PrivacyRoute />} />
              <Route path="/terms" element={<TermsRoute />} />

              <Route element={<RequireAnonymous />}>
                <Route path="/login" element={<LoginRoute />} />
                <Route path="/register" element={<RegisterRoute />} />
                <Route path="/forgot-password" element={<ForgotPasswordRoute />} />
                <Route path="/reset-password" element={<ResetPasswordRoute />} />
              </Route>

              <Route element={<RequireAuth />}>
                {/* No bottom tabs, no floating Log action, no nav link anywhere —
                    see the comment atop AdminMetricsRoute.tsx. */}
                <Route path="/admin/metrics" element={<AdminMetricsRoute />} />

                <Route element={<AppShell />}>
                  <Route path="/" element={<Navigate to="/entries" replace />} />
                  <Route path="/log" element={<LogRoute />} />
                  <Route path="/log/procedure" element={<LogProcedureRoute />} />
                  {/* Editing a stored procedure. The entry list links here. */}
                  <Route path="/log/procedure/:procedureId" element={<LogProcedureRoute />} />
                  <Route path="/log/case" element={<LogCaseRoute />} />
                  {/* Editing a stored case. Phase 11's entry list links here. */}
                  <Route path="/log/case/:caseId" element={<LogCaseRoute />} />
                  <Route path="/log/edt" element={<LogEdtRoute />} />
                  {/* Editing a stored EDT entry. The entry list links here. */}
                  <Route path="/log/edt/:edtId" element={<LogEdtRoute />} />
                  <Route path="/log/communication" element={<LogCommunicationRoute />} />
                  {/* Editing a stored Communication entry. The entry list links here. */}
                  <Route
                    path="/log/communication/:communicationId"
                    element={<LogCommunicationRoute />}
                  />
                  {/* Editing only — anaesthetic cases arrive by import. */}
                  <Route
                    path="/log/anaesthetic/:anaestheticId"
                    element={<EditAnaestheticRoute />}
                  />
                  <Route path="/entries" element={<EntriesRoute />} />
                  <Route path="/dashboard" element={<DashboardRoute />} />
                  <Route path="/report" element={<ReportRoute />} />
                  <Route path="/import" element={<ImportRoute />} />
                  <Route path="/settings" element={<SettingsRoute />} />

                  {/* Never routed in a production build. */}
                  {import.meta.env.DEV ? (
                    <Route path="/styleguide" element={<StyleguideRoute />} />
                  ) : null}

                  <Route path="*" element={<Navigate to="/entries" replace />} />
                </Route>
              </Route>
            </Routes>
          </ToastProvider>
        </SyncProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
